import { ReactNode, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Box, Typography, Button, Stack } from '@mui/material';
import { FullPageWandLoader } from '../FullPageWandLoader';
import { TestIds } from '../../constants';
import { useLoggerWithContext } from '../../services/logger/loggerContext';

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, error, login, token } = useAuth();
  const logger = useLoggerWithContext();

  // Log authentication errors
  useEffect(() => {
    if (error) {
      logger.error('Authentication error occurred', {
        auth_event: 'auth_error',
        error_message: error.message,
        error_name: error.name,
        current_path: window.location.pathname,
        current_search: window.location.search,
      });
    }
  }, [error, logger]);

  // Log when user is not authenticated and needs redirect
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !error) {
      logger.warn('User not authenticated, redirecting to login', {
        auth_event: 'not_authenticated_redirect',
        current_path: window.location.pathname,
        current_search: window.location.search,
        has_session_storage: !!window.sessionStorage.length,
        has_local_storage: !!window.localStorage.length,
      });
    }
  }, [isLoading, isAuthenticated, error, logger]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <Box data-testid={TestIds.AUTH_LOADING}>
        <FullPageWandLoader />
      </Box>
    );
  }

  // Show error state if authentication failed
  if (error) {
    return (
      <Stack
        data-testid={TestIds.AUTH_ERROR}
        alignItems="center"
        justifyContent="center"
        spacing={2}
        sx={{
          height: '100vh',
          p: 3,
        }}
      >
        <Typography variant="h5" color="error">
          Authentication Error
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {error.message}
        </Typography>
        <Button variant="contained" onClick={login}>
          Try Again
        </Button>
      </Stack>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    login();
    return (
      <Box data-testid={TestIds.AUTH_REDIRECTING}>
        <FullPageWandLoader label="Redirecting to login..." />
      </Box>
    );
  }

  // Wait for the access token before rendering children — downstream
  // suspense queries (e.g. EntityProvider) depend on it being available.
  if (!token) {
    return (
      <Box data-testid={TestIds.AUTH_LOADING}>
        <FullPageWandLoader />
      </Box>
    );
  }

  // User is authenticated and token is ready, render children
  return <>{children}</>;
}
